// frontend/src/pages/crm/Clients.js
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useBranding } from '../../contexts/BrandingContext';
import { clientAPI } from '../../utils/crmApi';
import {
    UserPlusIcon,
    EnvelopeIcon,
    PhoneIcon,
    TrashIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useDialog, ConfirmationDialog } from '../../components/kibo-ui/dialog';
import { EmptyStates } from '../../components/ui/empty-state';
import { useCRM } from '../../contexts/CRMContext';
import { PERMISSIONS, hasPermission } from '../../utils/permissions';
import {
    TableProvider,
    Table,
    TableHeader,
    TableSearch,
    TableHead,
    TableBody,
    TableRow,
    TableHeaderCell,
    TableCell,
    TableFooter,
    StatusBadge,
    TableAvatar
} from '../../components/kibo-ui/table';

const Clients = () => {
    const { openDialog } = useDialog();
    const { branding } = useBranding();
    const { user, currentAgency } = useCRM();
    const navigate = useNavigate();
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [pagination, setPagination] = useState({ page: 1, limit: 20 });
    const [selectedClients, setSelectedClients] = useState([]);
    const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
    const isRequestInFlightRef = useRef(false);

    useEffect(() => {
        // Reset request in-flight flag when dependencies change
        isRequestInFlightRef.current = false;
        
        loadClients();
    }, [search, statusFilter, pagination.page]);

    const loadClients = async () => {
        // Prevent duplicate requests (React StrictMode causes double renders)
        if (isRequestInFlightRef.current) {
            return;
        }
        
        // Check if token exists before making request
        const token = localStorage.getItem('crm_token');
        if (!token) {
            console.log('⚠️ No token available, skipping request');
            setLoading(false);
            return;
        }
        
        try {
            // Mark request as in flight
            isRequestInFlightRef.current = true;
            setLoading(true);
            
            const params = {
                page: pagination.page,
                limit: pagination.limit,
                ...(search && { search }),
                ...(statusFilter && { status: statusFilter })
            };
            
            const response = await clientAPI.getAll(params);
            
            // Check if component unmounted or another request started
            if (!isRequestInFlightRef.current) {
                return;
            }
            
            if (response?.data?.success) {
                setClients(response.data.data || []);
                if (response.data.pagination) {
                    setPagination(response.data.pagination);
                }
            }
        } catch (error) {
            // Don't log errors if request was cancelled or if it's a 401 without a token
            if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
                return;
            }
            
            // Don't show toast for 401 errors during initial load (expected with StrictMode)
            if (error.response?.status === 401) {
                // Only show error if we have a token (real auth failure)
                if (token) {
                    console.error('❌ Authentication error:', error.response?.data?.message);
                }
                return;
            }
            
            console.error('❌ Failed to load clients:', error);
            toast.error('Failed to load clients');
        } finally {
            isRequestInFlightRef.current = false;
            setLoading(false);
        }
    };

    const handleSelectClient = (clientId) => {
        setSelectedClients(prev => 
            prev.includes(clientId) 
                ? prev.filter(id => id !== clientId)
                : [...prev, clientId]
        );
    };

    const handleSelectAll = () => {
        if (selectedClients.length === clients.length) {
            setSelectedClients([]);
        } else {
            setSelectedClients(clients.map(client => client._id));
        }
    };

    const handleBulkDelete = () => {
        if (selectedClients.length === 0) return;
        
        openDialog({
            title: 'Delete Clients',
            description: `Are you sure you want to delete ${selectedClients.length} client(s)? This action cannot be undone and will remove all associated data.`,
            confirmText: 'Delete Clients',
            cancelText: 'Cancel',
            variant: 'danger',
            onConfirm: async () => {
                try {
                    setBulkDeleteLoading(true);
                    await clientAPI.deleteMultiple(selectedClients);
                    toast.success(`${selectedClients.length} client(s) deleted successfully`);
                    setSelectedClients([]);
                    loadClients();
                } catch (error) {
                    console.error('Error deleting clients:', error);
                    toast.error('Failed to delete clients: ' + error.message);
                } finally {
                    setBulkDeleteLoading(false);
                }
            }
        });
    };

    const handleInviteClient = async (clientId) => {
        try {
            await clientAPI.invite(clientId);
            toast.success('Invitation sent successfully!');
        } catch (error) {
            toast.error('Failed to send invitation');
        }
    };

    // Define columns for the table
    const columns = [
        { accessor: 'select', label: '', sortable: false, width: 'w-12' },
        { accessor: 'name', label: 'Client', sortable: true },
        { accessor: 'email', label: 'Contact', sortable: true },
        { accessor: 'status', label: 'Status', sortable: true },
        { accessor: 'location', label: 'Location', sortable: true },
        { accessor: 'actions', label: 'Actions', sortable: false }
    ];

    // Debug logging
    console.log('🔍 Clients component state:', {
        loading,
        clientsCount: clients.length,
        clients: clients,
        search,
        statusFilter,
        pagination
    });

    const renderPortalBadge = (c) => {
        const hasUser = !!(c?.userId || c?.portalAccess?.enabled);
        if (hasUser) {
            return (
                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: '#10B981', color: 'white' }}>
                    Registered
                </span>
            );
        }
        if (c?.portalAccess?.invitedAt || c?.invitedAt) {
            return (
                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: '#F59E0B', color: 'white' }}>
                    Invited
                </span>
            );
        }
        return (
            <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: '#6B7280', color: 'white' }}>
                Not invited
            </span>
        );
    };

    return (
        <div className="space-y-6" data-tour="client-management">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-foreground">Clients</h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Manage your clients and their relocation journeys
                    </p>
                </div>
                <div className="mt-4 sm:mt-0 flex items-center gap-3">
                    {selectedClients.length > 0 && (
                        <button
                            onClick={handleBulkDelete}
                            disabled={bulkDeleteLoading}
                            className="inline-flex items-center px-4 py-2 border rounded-lg shadow-sm text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 hover:opacity-90"
                            style={{ 
                                borderColor: `${branding.accentColor}40`,
                                color: branding.accentColor,
                                backgroundColor: `${branding.accentColor}20`,
                                '--tw-ring-color': branding.accentColor
                            }}
                        >
                            <TrashIcon className="h-5 w-5 mr-2" />
                            {bulkDeleteLoading ? 'Deleting...' : `Delete ${selectedClients.length}`}
                        </button>
                    )}
                    {hasPermission(user, PERMISSIONS.CREATE_CLIENT, currentAgency?._id) && (
                        <Link
                            to="/clients/new"
                            className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2"
                            style={{
                                backgroundColor: branding.primaryColor,
                                '--tw-ring-color': branding.primaryColor
                            }}
                        >
                            <UserPlusIcon className="h-5 w-5 mr-2" />
                            Add Client
                        </Link>
                    )}
                </div>
            </div>

            {/* Enhanced Table with Kibo UI */}
            <TableProvider 
                data={clients} 
                columns={columns}
                onSort={(key, direction) => console.log('🔄 Table sort:', { key, direction })}
                onFilter={(key, value) => console.log('🔍 Table filter:', { key, value })}
            >
                <Table>
                    <TableHeader>
                        <TableSearch placeholder="Search clients by name or email..." />
                    </TableHeader>
                    
                    <div className="bg-card rounded-xl shadow-sm border border-border overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-border">
                            <TableHead>
                                <tr>
                                    {columns.map((column) => (
                                        <TableHeaderCell key={column.accessor} column={column}>
                                            {column.accessor === 'select' ? (
                                                <input
                                                    type="checkbox"
                                                    checked={selectedClients.length === clients.length && clients.length > 0}
                                                    onChange={handleSelectAll}
                                                    className="h-4 w-4 border-input rounded focus:ring-2 focus:border-transparent"
                                                    style={{ 
                                                        color: branding.primaryColor,
                                                        '--tw-ring-color': branding.primaryColor
                                                    }}
                                                />
                                            ) : (
                                                column.label
                                            )}
                                        </TableHeaderCell>
                                    ))}
                                </tr>
                            </TableHead>
                            <TableBody>
                {loading ? (
                                    <tr>
                                        <td colSpan={columns.length} className="px-6 py-12 text-center">
                                            <div className="flex items-center justify-center">
                                                <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: branding.primaryColor }}></div>
                    </div>
                                        </td>
                                    </tr>
                ) : clients.length === 0 ? (
                                    <tr>
                                        <td colSpan={columns.length} className="px-6 py-12">
                                            <EmptyStates.NoClients 
                                                onAddClient={() => navigate('/clients/new')}
                                                branding={branding}
                                            />
                                        </td>
                                    </tr>
                                ) : (
                                    clients.map((client) => (
                                        <TableRow 
                                            key={client._id}
                                            onClick={() => navigate(`/clients/${client._id}`)}
                                            className="cursor-pointer"
                                        >
                                            <TableCell className="w-12" onClick={(e) => e.stopPropagation()}>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedClients.includes(client._id)}
                                                    onChange={(e) => {
                                                        e.stopPropagation();
                                                        handleSelectClient(client._id);
                                                    }}
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="h-4 w-4 border-input rounded focus:ring-2"
                                                    style={{ 
                                                        accentColor: branding.primaryColor,
                                                        '--tw-ring-color': branding.primaryColor 
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center">
                                                    <TableAvatar 
                                                        name={`${client.firstName || ''} ${client.lastName || ''}`.trim() || client.name || ''}
                                                        src={client.profileImage}
                                                        className="mr-3"
                                                    />
                                                    <div>
                                                        <div className="flex items-center flex-wrap gap-2">
                                                        <span
                                                            className="text-sm font-medium text-foreground"
                                                        >
                                                            {`${client.firstName || ''} ${client.lastName || ''}`.trim() || client.name || 'Unknown Client'}
                                                        </span>
                                                        </div>
                                                        {client.accountId && (
                                                            <div className="text-xs text-muted-foreground truncate max-w-[220px] sm:max-w-[300px]">
                                                                {client.accountId.name}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="text-sm text-foreground">
                                                    <div className="flex items-center">
                                                        <EnvelopeIcon className="h-4 w-4 text-muted-foreground mr-2" />
                                                        {hasPermission(user, PERMISSIONS.VIEW_PII, currentAgency?._id) ? client.email : '•••••••'}
                                                    </div>
                                                    {client.phone && (
                                                        <div className="flex items-center mt-1">
                                                            <PhoneIcon className="h-4 w-4 text-muted-foreground mr-2" />
                                                            {hasPermission(user, PERMISSIONS.VIEW_PII, currentAgency?._id) ? client.phone : '•••••••'}
                                                        </div>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                <StatusBadge status={client.status} />
                                                    {renderPortalBadge(client)}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <span className="text-sm text-muted-foreground">
                                                    {client.countryResidence || client.countryNationality || '—'}
                                                </span>
                                            </TableCell>
                                            <TableCell onClick={(e) => e.stopPropagation()}>
                                                <div className="flex items-center space-x-2">
                                                <Link
                                                    to={`/clients/${client._id}`}
                                                    className="text-sm hover:opacity-80 transition-opacity"
                                                    style={{ color: branding.primaryColor }}
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    View
                                                </Link>
                                                {!client.portalAccess?.enabled && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleInviteClient(client._id);
                                                        }}
                                                        className="text-sm hover:opacity-80 transition-opacity"
                                                        style={{ color: branding.secondaryColor }}
                                                    >
                                                        Invite
                                                    </button>
                                                )}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                            </table>
                        </div>
                        </div>

                    <TableFooter />
                </Table>
            </TableProvider>

        </div>
    );
};

export default Clients;





